import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useTranslation } from 'react-i18next';
import type { InquiryDto, OfferDto, ProjectDto, ProjectLastActivityKind, ProjectStatus } from '@paladin-monorepo/common';
import { Calendar } from 'lucide-react';
import { formatDate } from '@/utils/formatDate';
import { formatRelativeTime } from '@/utils/formatRelativeTime';
import { getAppFormatLocale } from '@/utils/locale';

function statusVariant(status: ProjectStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'NotStarted':
      return 'secondary';
    case 'InProgress':
      return 'default';
    case 'Completed':
      return 'outline';
    case 'Closed':
      return 'outline';
    default:
      return 'outline';
  }
}

function lastActivityKindKey(kind: ProjectLastActivityKind): string {
  return `workspace.header.lastActivityKind.${kind}`;
}

function formatMoney(amount: number, currency: string) {
  return new Intl.NumberFormat(getAppFormatLocale(), { style: 'currency', currency }).format(amount);
}

export function ProjectHeader({
  project,
  inquiry,
  offer,
}: {
  project: ProjectDto;
  inquiry?: InquiryDto;
  offer?: OfferDto;
}) {
  const { t, i18n } = useTranslation();
  const fin = project.financialOverview;
  const activityLine =
    project.lastActivityAt && project.lastActivityKind
      ? t('workspace.header.lastActivity', {
          relative: formatRelativeTime(project.lastActivityAt, i18n.language),
          kind: t(lastActivityKindKey(project.lastActivityKind)),
        })
      : offer?.updatedAt
        ? t('workspace.header.lastUpdated', { date: formatDate(offer.updatedAt) })
        : null;

  return (
    <header className="rounded-xl border p-4 mb-4 bg-background">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-grow">
          <h1 className="text-xl font-semibold text-foreground">
            {inquiry?.title ?? project.title}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
            <Badge variant={statusVariant(project.status)}>
              {t(`workspace.status.${project.status}`)}
            </Badge>
            <div className="inline-flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>
                {project.startDate
                  ? t('generic.fromDate', { date: formatDate(project.startDate) })
                  : t('generic.unspecified')}{' '}
                {project.endDate
                  ? t('generic.toDate', { date: formatDate(project.endDate) })
                  : ''}
              </span>
            </div>
          </div>
        </div>
        <div className="w-full sm:w-64">
          <div className="flex justify-between text-xs mb-1 text-muted-foreground">
            <span className="font-medium">{t('workspace.header.completion')}</span>
            <span>{project.progress}%</span>
          </div>
          <Progress value={project.progress} />
          {activityLine && (
            <p className="text-[10px] text-muted-foreground text-right mt-1">
              {activityLine}
            </p>
          )}
        </div>
      </div>
      {fin != null && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t text-sm">
          <div>
            <div className="text-xs text-muted-foreground">{t('workspace.header.finance.totalBudget')}</div>
            <div className="font-medium tabular-nums">{formatMoney(fin.totalBudgetGross, fin.currency)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{t('workspace.header.finance.escrowBalance')}</div>
            <div className="font-medium tabular-nums">
              {fin.escrowBalance != null ? formatMoney(fin.escrowBalance, fin.currency) : '-'}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{t('workspace.header.finance.paidOutPlatform')}</div>
            <div className="font-medium tabular-nums">{formatMoney(fin.totalPaidOut, fin.currency)}</div>
            <p className="text-[10px] text-muted-foreground mt-0.5">{t('workspace.header.finance.paidOutHint')}</p>
          </div>
        </div>
      )}
    </header>
  );
}
